import React, { useState, useEffect } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  ScrollView,
  Platform,
  Image,
  Switch,
  Linking } from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import { ParkingCircle, Accessibility, Wifi, Shirt, Coffee, CreditCard, Banknote, Smartphone, FileText, Image as ImageIcon, Clock, MapPin, Sparkles, Store, Check, Circle, LucideIcon, HandCoins, Users, ShoppingBag } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { startConnectOnboarding, getConnectDashboardLink } from '../lib/stripe'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketSettings'>
  route: RouteProp<RootStackParamList, 'MarketSettings'>
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const AMENITIES: { key: string; icon: LucideIcon; labelKey: string }[] = [
  { key: 'parking', icon: ParkingCircle, labelKey: 'parking' },
  { key: 'wheelchair', icon: Accessibility, labelKey: 'wheelchair_accessible' },
  { key: 'wifi', icon: Wifi, labelKey: 'free_wifi' },
  { key: 'fitting_room', icon: Shirt, labelKey: 'fitting_room' },
  { key: 'cafe', icon: Coffee, labelKey: 'cafe' },
  { key: 'card_payment', icon: CreditCard, labelKey: 'card_payment' },
  { key: 'cash_payment', icon: Banknote, labelKey: 'cash_payment' },
  { key: 'mobilepay', icon: Smartphone, labelKey: 'mobilepay' },
]

const SECTION_ICONS: Record<string, LucideIcon> = {
  basic: FileText,
  services: ShoppingBag,
  payments: CreditCard,
  images: ImageIcon,
  hours: Clock,
  contact: MapPin,
  amenities: Sparkles }

interface OpeningHours {
  [key: string]: { open: string; close: string; closed: boolean }
}

interface MarketData {
  name: string
  description: string
  addressStreet: string
  addressCity: string
  addressPostalCode: string
  phone: string
  email: string
  websiteUrl: string
  logoUrl: string | null
  coverImageUrl: string | null
  openingHours: OpeningHours
  amenities: string[]
  commissionPercentage: string
  stripeConnectAccountId: string | null
  stripeChargesEnabled: boolean
  stripePayoutsEnabled: boolean
  stripeDetailsSubmitted: boolean
  // Service modes
  allowSelfService: boolean
  offersFullService: boolean
  fullServiceInfo: string
  acceptsOffers: boolean
  offerDropoffInfo: string
}

const defaultOpeningHours: OpeningHours = {
  monday: { open: '10:00', close: '18:00', closed: false },
  tuesday: { open: '10:00', close: '18:00', closed: false },
  wednesday: { open: '10:00', close: '18:00', closed: false },
  thursday: { open: '10:00', close: '18:00', closed: false },
  friday: { open: '10:00', close: '18:00', closed: false },
  saturday: { open: '10:00', close: '16:00', closed: false },
  sunday: { open: '', close: '', closed: true } }

export default function MarketSettingsScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('basic')
  const [connectingStripe, setConnectingStripe] = useState(false)
  const [loadingDashboard, setLoadingDashboard] = useState(false)

  const [data, setData] = useState<MarketData>({
    name: '',
    description: '',
    addressStreet: '',
    addressCity: '',
    addressPostalCode: '',
    phone: '',
    email: '',
    websiteUrl: '',
    logoUrl: null,
    coverImageUrl: null,
    openingHours: defaultOpeningHours,
    amenities: [],
    commissionPercentage: '15',
    stripeConnectAccountId: null,
    stripeChargesEnabled: false,
    stripePayoutsEnabled: false,
    stripeDetailsSubmitted: false,
    // Service modes
    allowSelfService: true,
    offersFullService: false,
    fullServiceInfo: '',
    acceptsOffers: false,
    offerDropoffInfo: '' })

  useEffect(() => {
    fetchMarket()
  }, [marketId])

  const fetchMarket = async () => {
    try {
      const { data: market, error } = await supabase
        .from('flea_markets')
        .select('*')
        .eq('id', marketId)
        .single()

      if (error) throw error

      // Fallback to metadata for stores created before columns were populated
      const metadata = market.metadata || {}

      setData({
        name: market.name || '',
        description: market.description || '',
        addressStreet: market.address_street || metadata.address || '',
        addressCity: market.address_city || '',
        addressPostalCode: market.address_postal_code || metadata.postal_code || '',
        phone: market.phone || metadata.phone || '',
        email: market.email || metadata.email || '',
        websiteUrl: market.website_url || metadata.website || '',
        logoUrl: market.logo_url || null,
        coverImageUrl: market.cover_image_url || null,
        openingHours: market.opening_hours || defaultOpeningHours,
        amenities: market.settings?.amenities || [],
        commissionPercentage: market.commission_percentage?.toString() || '15',
        stripeConnectAccountId: market.stripe_connect_account_id || null,
        stripeChargesEnabled: market.stripe_charges_enabled || false,
        stripePayoutsEnabled: market.stripe_payouts_enabled || false,
        stripeDetailsSubmitted: market.stripe_details_submitted || false,
        // Service modes
        allowSelfService: market.allow_self_registration !== false, // Default true
        offersFullService: market.offers_full_service || false,
        fullServiceInfo: market.full_service_info || '',
        acceptsOffers: market.accepts_offers || false,
        offerDropoffInfo: market.offer_dropoff_info || '' })
    } catch (error) {
      console.error('Error fetching market:', error)
      showAlert(t('error') || 'Error', t('market_not_found') || 'Market not found')
    } finally {
      setLoading(false)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        onConfirm()
      }
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', style: 'destructive', onPress: onConfirm },
      ])
    }
  }

  const pickImage = async (type: 'logo' | 'cover') => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: type === 'logo' ? [1, 1] : [16, 9],
        quality: 0.8 })

      if (!result.canceled && result.assets[0]) {
        await uploadImage(result.assets[0].uri, type)
      }
    } catch (error) {
      console.error('Error picking image:', error)
      showAlert(t('error') || 'Error', 'Failed to pick image')
    }
  }

  const uploadImage = async (uri: string, type: 'logo' | 'cover') => {
    if (type === 'logo') setUploadingLogo(true)
    else setUploadingCover(true)

    try {
      const response = await fetch(uri)
      const blob = await response.blob()
      const fileName = `${marketId}/${type}-${Date.now()}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('store-images')
        .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage.from('store-images').getPublicUrl(fileName)

      if (type === 'logo') {
        setData(prev => ({ ...prev, logoUrl: urlData.publicUrl }))
      } else {
        setData(prev => ({ ...prev, coverImageUrl: urlData.publicUrl }))
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      showAlert(t('error') || 'Error', 'Failed to upload image')
    } finally {
      if (type === 'logo') setUploadingLogo(false)
      else setUploadingCover(false)
    }
  }

  const updateOpeningHours = (day: string, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value } } }))
  }

  const toggleAmenity = (amenityKey: string) => {
    setData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityKey)
        ? prev.amenities.filter(a => a !== amenityKey)
        : [...prev.amenities, amenityKey] }))
  }

  const saveMarket = async () => {
    if (!data.name.trim()) {
      showAlert(t('error') || 'Error', t('enter_store_name') || 'Please enter a name')
      return
    }

    const commissionNum = parseFloat(data.commissionPercentage)
    if (isNaN(commissionNum) || commissionNum < 0 || commissionNum > 100) {
      showAlert(t('error') || 'Error', t('invalid_commission') || 'Commission must be between 0 and 100')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('flea_markets')
        .update({
          name: data.name.trim(),
          description: data.description.trim() || null,
          address_street: data.addressStreet.trim() || null,
          address_city: data.addressCity.trim() || null,
          address_postal_code: data.addressPostalCode.trim() || null,
          phone: data.phone.trim() || null,
          email: data.email.trim() || null,
          website_url: data.websiteUrl.trim() || null,
          logo_url: data.logoUrl,
          cover_image_url: data.coverImageUrl,
          opening_hours: data.openingHours,
          settings: { amenities: data.amenities },
          commission_percentage: commissionNum,
          // Service modes
          allow_self_registration: data.allowSelfService,
          offers_full_service: data.offersFullService,
          full_service_info: data.fullServiceInfo.trim() || null,
          accepts_offers: data.acceptsOffers,
          offer_dropoff_info: data.offerDropoffInfo.trim() || null })
        .eq('id', marketId)

      if (error) throw error

      showAlert(t('success') || 'Success', t('store_updated') || 'Settings saved!')
    } catch (error: any) {
      console.error('Update market error:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  const handleStripeConnect = async () => {
    setConnectingStripe(true)
    try {
      const result = await startConnectOnboarding(marketId)

      if (result.error) {
        showAlert(t('error') || 'Error', result.error)
        return
      }

      if (result.url) {
        if (Platform.OS === 'web') {
          window.open(result.url, '_blank')
        } else {
          await Linking.openURL(result.url)
        }
      }
    } catch (error: any) {
      console.error('Stripe connect error:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to start Stripe onboarding')
    } finally {
      setConnectingStripe(false)
    }
  }

  const handleStripeDashboard = async () => {
    setLoadingDashboard(true)
    try {
      const result = await getConnectDashboardLink(marketId)

      if (result.error) {
        if (result.needsOnboarding) {
          showAlert(t('error') || 'Error', t('complete_stripe_setup') || 'Please complete Stripe setup first')
        } else {
          showAlert(t('error') || 'Error', result.error)
        }
        return
      }

      if (result.url) {
        if (Platform.OS === 'web') {
          window.open(result.url, '_blank')
        } else {
          await Linking.openURL(result.url)
        }
      }
    } catch (error: any) {
      console.error('Stripe dashboard error:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to open Stripe dashboard')
    } finally {
      setLoadingDashboard(false)
    }
  }

  const deleteMarket = async () => {
    confirmAction(
      t('delete') || 'Delete',
      t('delete_market_confirm') || 'Are you sure you want to delete this flea market? All spaces, reservations, and products will also be deleted.',
      async () => {
        setDeleting(true)
        try {
          // Delete products
          await supabase
            .from('products')
            .delete()
            .eq('flea_market_id', marketId)

          // Delete reservations
          await supabase
            .from('space_reservations')
            .delete()
            .eq('flea_market_id', marketId)

          // Delete spaces
          await supabase
            .from('market_spaces')
            .delete()
            .eq('flea_market_id', marketId)

          // Delete seller memberships
          await supabase
            .from('seller_market_memberships')
            .delete()
            .eq('flea_market_id', marketId)

          // Delete staff
          await supabase
            .from('flea_market_staff')
            .delete()
            .eq('flea_market_id', marketId)

          // Delete the market itself
          const { error } = await supabase
            .from('flea_markets')
            .delete()
            .eq('id', marketId)

          if (error) throw error

          showAlert(t('success') || 'Success', t('market_deleted') || 'Market deleted')
          navigation.navigate('MerchantStoreSelect')
        } catch (error: any) {
          console.error('Delete market error:', error)
          showAlert(t('error') || 'Error', error.message || 'Failed to delete')
        } finally {
          setDeleting(false)
        }
      }
    )
  }

  const renderSectionTab = (key: string, label: string) => {
    const IconComponent = SECTION_ICONS[key] || FileText
    const isActive = activeSection === key
    return (
      <TouchableOpacity
        key={key}
        style={[styles.sectionTab, isActive && styles.sectionTabActive]}
        onPress={() => setActiveSection(key)}
      >
        <IconComponent size={16} color={isActive ? '#fff' : theme.colors.text.secondary} />
        <Text style={[styles.sectionTabText, isActive && styles.sectionTabTextActive]}>
          {label}
        </Text>
      </TouchableOpacity>
    )
  }

  const renderBasicSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('market_name') || 'Market Name'} *</Text>
        <TextInput
          style={styles.input}
          value={data.name}
          onChangeText={(text) => setData(prev => ({ ...prev, name: text }))}
          placeholder={t('market_name_placeholder') || 'e.g. Sunday Flea Market'}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('description') || 'Description'}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={data.description}
          onChangeText={(text) => setData(prev => ({ ...prev, description: text }))}
          placeholder={t('market_description_placeholder') || 'Describe your flea market...'}
          placeholderTextColor={theme.colors.text.secondary}
          multiline
          numberOfLines={4}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('commission_percentage') || 'Commission %'}</Text>
        <TextInput
          style={styles.input}
          value={data.commissionPercentage}
          onChangeText={(text) => setData(prev => ({ ...prev, commissionPercentage: text }))}
          placeholder="15"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="numeric"
        />
        <Text style={styles.hint}>
          {t('commission_hint') || 'Percentage kept by the market on sales'}
        </Text>
      </View>
    </View>
  )

  const renderImagesSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('store_logo') || 'Market Logo'}</Text>
        <TouchableOpacity style={styles.imageUploadBox} onPress={() => pickImage('logo')} disabled={uploadingLogo}>
          {uploadingLogo ? (
            <Spinner size="small" />
          ) : data.logoUrl ? (
            <Image source={{ uri: data.logoUrl }} style={styles.logoPreview} />
          ) : (
            <View style={styles.imagePlaceholder}>
              <View style={styles.imagePlaceholderIconContainer}>
                <ImageIcon size={24} color={theme.colors.text.secondary} />
              </View>
              <Text style={styles.imagePlaceholderText}>{t('tap_to_upload') || 'Tap to upload'}</Text>
            </View>
          )}
        </TouchableOpacity>
        <Text style={styles.hint}>{t('logo_hint') || 'Square image recommended (1:1)'}</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('cover_image') || 'Cover Image'}</Text>
        <TouchableOpacity style={styles.coverUploadBox} onPress={() => pickImage('cover')} disabled={uploadingCover}>
          {uploadingCover ? (
            <Spinner size="small" />
          ) : data.coverImageUrl ? (
            <Image source={{ uri: data.coverImageUrl }} style={styles.coverPreview} />
          ) : (
            <View style={styles.imagePlaceholder}>
              <View style={styles.imagePlaceholderIconContainer}>
                <Store size={24} color={theme.colors.text.secondary} />
              </View>
              <Text style={styles.imagePlaceholderText}>{t('tap_to_upload') || 'Tap to upload'}</Text>
            </View>
          )}
        </TouchableOpacity>
        <Text style={styles.hint}>{t('cover_hint') || 'Wide image recommended (16:9)'}</Text>
      </View>
    </View>
  )

  const renderHoursSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionDescription}>
        {t('opening_hours_desc') || 'Set your market opening hours for each day of the week'}
      </Text>
      {WEEKDAYS.map((day) => (
        <View key={day} style={styles.hoursRow}>
          <View style={styles.dayLabel}>
            <Text style={styles.dayText}>{t(day) || day}</Text>
          </View>
          <View style={styles.hoursInputs}>
            {data.openingHours[day]?.closed ? (
              <Text style={styles.closedText}>{t('closed') || 'Closed'}</Text>
            ) : (
              <>
                <TextInput
                  style={styles.timeInput}
                  value={data.openingHours[day]?.open || ''}
                  onChangeText={(text) => updateOpeningHours(day, 'open', text)}
                  placeholder="10:00"
                  placeholderTextColor={theme.colors.text.secondary}
                />
                <Text style={styles.timeSeparator}>-</Text>
                <TextInput
                  style={styles.timeInput}
                  value={data.openingHours[day]?.close || ''}
                  onChangeText={(text) => updateOpeningHours(day, 'close', text)}
                  placeholder="18:00"
                  placeholderTextColor={theme.colors.text.secondary}
                />
              </>
            )}
          </View>
          <Switch
            value={!data.openingHours[day]?.closed}
            onValueChange={(value) => updateOpeningHours(day, 'closed', !value)}
            trackColor={{ false: theme.colors.ui.borderLight, true: theme.colors.merchant.light }}
            thumbColor={data.openingHours[day]?.closed ? theme.colors.text.secondary : theme.colors.merchant.primary}
          />
        </View>
      ))}
    </View>
  )

  const renderContactSection = () => (
    <View style={styles.section}>
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('address_street') || 'Street Address'}</Text>
        <TextInput
          style={styles.input}
          value={data.addressStreet}
          onChangeText={(text) => setData(prev => ({ ...prev, addressStreet: text }))}
          placeholder={t('address_street_placeholder') || 'e.g. Mannerheimintie 10'}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      <View style={styles.rowInputs}>
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={styles.label}>{t('postal_code') || 'Postal Code'}</Text>
          <TextInput
            style={styles.input}
            value={data.addressPostalCode}
            onChangeText={(text) => setData(prev => ({ ...prev, addressPostalCode: text }))}
            placeholder="00100"
            placeholderTextColor={theme.colors.text.secondary}
          />
        </View>
        <View style={[styles.inputGroup, { flex: 2, marginLeft: 12 }]}>
          <Text style={styles.label}>{t('city') || 'City'}</Text>
          <TextInput
            style={styles.input}
            value={data.addressCity}
            onChangeText={(text) => setData(prev => ({ ...prev, addressCity: text }))}
            placeholder={t('city_placeholder') || 'Helsinki'}
            placeholderTextColor={theme.colors.text.secondary}
          />
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('phone') || 'Phone'}</Text>
        <TextInput
          style={styles.input}
          value={data.phone}
          onChangeText={(text) => setData(prev => ({ ...prev, phone: text }))}
          placeholder="+358 40 123 4567"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="phone-pad"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('email') || 'Email'}</Text>
        <TextInput
          style={styles.input}
          value={data.email}
          onChangeText={(text) => setData(prev => ({ ...prev, email: text }))}
          placeholder="market@example.com"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="email-address"
          autoCapitalize="none"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('website') || 'Website'}</Text>
        <TextInput
          style={styles.input}
          value={data.websiteUrl}
          onChangeText={(text) => setData(prev => ({ ...prev, websiteUrl: text }))}
          placeholder="https://www.example.com"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="url"
          autoCapitalize="none"
        />
      </View>
    </View>
  )

  const renderAmenitiesSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionDescription}>
        {t('amenities_desc') || 'Select the amenities available at your market'}
      </Text>
      <View style={styles.amenitiesGrid}>
        {AMENITIES.map((amenity) => {
          const isSelected = data.amenities.includes(amenity.key)
          const IconComponent = amenity.icon
          return (
            <TouchableOpacity
              key={amenity.key}
              style={[styles.amenityItem, isSelected && styles.amenityItemSelected]}
              onPress={() => toggleAmenity(amenity.key)}
            >
              <View style={styles.amenityIconContainer}>
                <IconComponent size={20} color={isSelected ? theme.colors.merchant.primary : theme.colors.text.secondary} />
              </View>
              <Text style={[styles.amenityLabel, isSelected && styles.amenityLabelSelected]}>
                {t(amenity.labelKey) || amenity.labelKey}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )

  const renderServicesSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionDescription}>
        {t('services_section_desc') || 'Choose what selling options you offer. You can enable any combination.'}
      </Text>

      {/* Service Mode 1: Self-service */}
      <View style={styles.serviceModeCard}>
        <View style={styles.serviceModeHeader}>
          <View style={styles.serviceModeIconContainer}>
            <Store size={24} color={data.allowSelfService ? theme.colors.merchant.primary : '#999'} />
          </View>
          <View style={styles.serviceModeInfo}>
            <Text style={styles.serviceModeTitle}>{t('self_service_mode') || 'Itse myynti'}</Text>
            <Text style={styles.serviceModeDesc}>
              {t('self_service_mode_desc') || 'Myyjät varaavat paikan ja myyvät itse'}
            </Text>
          </View>
          <Switch
            value={data.allowSelfService}
            onValueChange={(value) => setData(prev => ({ ...prev, allowSelfService: value }))}
            trackColor={{ false: '#ddd', true: `${theme.colors.merchant.primary}80` }}
            thumbColor={data.allowSelfService ? theme.colors.merchant.primary : '#f4f4f4'}
          />
        </View>
      </View>

      {/* Service Mode 2: Full-service */}
      <View style={styles.serviceModeCard}>
        <View style={styles.serviceModeHeader}>
          <View style={styles.serviceModeIconContainer}>
            <Users size={24} color={data.offersFullService ? theme.colors.merchant.primary : '#999'} />
          </View>
          <View style={styles.serviceModeInfo}>
            <Text style={styles.serviceModeTitle}>{t('full_service_mode') || 'Puolestamyynti'}</Text>
            <Text style={styles.serviceModeDesc}>
              {t('full_service_mode_desc') || 'Henkilökunta myy tuotteet myyjän puolesta'}
            </Text>
          </View>
          <Switch
            value={data.offersFullService}
            onValueChange={(value) => setData(prev => ({ ...prev, offersFullService: value }))}
            trackColor={{ false: '#ddd', true: `${theme.colors.merchant.primary}80` }}
            thumbColor={data.offersFullService ? theme.colors.merchant.primary : '#f4f4f4'}
          />
        </View>
        {data.offersFullService && (
          <View style={styles.serviceModeExpanded}>
            <Text style={styles.label}>{t('full_service_info') || 'Puolestamyynnin ohjeet'}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={data.fullServiceInfo}
              onChangeText={(text) => setData(prev => ({ ...prev, fullServiceInfo: text }))}
              placeholder={t('full_service_info_placeholder') || 'Esim. tuotteiden tuonti, provisio, maksuehdot...'}
              placeholderTextColor={theme.colors.text.secondary}
              multiline
              numberOfLines={3}
            />
          </View>
        )}
      </View>

      {/* Service Mode 3: Direct purchase / Offers */}
      <View style={styles.serviceModeCard}>
        <View style={styles.serviceModeHeader}>
          <View style={styles.serviceModeIconContainer}>
            <HandCoins size={24} color={data.acceptsOffers ? theme.colors.merchant.primary : '#999'} />
          </View>
          <View style={styles.serviceModeInfo}>
            <Text style={styles.serviceModeTitle}>{t('direct_purchase_mode') || 'Suoraosto'}</Text>
            <Text style={styles.serviceModeDesc}>
              {t('direct_purchase_mode_desc') || 'Osta tuotteita suoraan myyjiltä'}
            </Text>
          </View>
          <Switch
            value={data.acceptsOffers}
            onValueChange={(value) => setData(prev => ({ ...prev, acceptsOffers: value }))}
            trackColor={{ false: '#ddd', true: `${theme.colors.merchant.primary}80` }}
            thumbColor={data.acceptsOffers ? theme.colors.merchant.primary : '#f4f4f4'}
          />
        </View>
        {data.acceptsOffers && (
          <View style={styles.serviceModeExpanded}>
            <Text style={styles.label}>{t('dropoff_info') || 'Tuontiohjeet'}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={data.offerDropoffInfo}
              onChangeText={(text) => setData(prev => ({ ...prev, offerDropoffInfo: text }))}
              placeholder={t('dropoff_info_placeholder') || 'Milloin ja minne myyjät voivat tuoda hyväksytyt tavarat?'}
              placeholderTextColor={theme.colors.text.secondary}
              multiline
              numberOfLines={3}
            />
          </View>
        )}
      </View>

      {/* Commission info */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('commission_rate') || 'Provisio (%)'}</Text>
        <TextInput
          style={styles.input}
          value={data.commissionPercentage}
          onChangeText={(text) => setData(prev => ({ ...prev, commissionPercentage: text }))}
          placeholder="15"
          keyboardType="numeric"
          placeholderTextColor={theme.colors.text.secondary}
        />
        <Text style={styles.hint}>
          {t('commission_hint') || 'Osuus myynnistä puolestamyynnin ja suoraoston tapauksissa'}
        </Text>
      </View>
    </View>
  )

  const renderPaymentsSection = () => (
    <View style={styles.section}>
      <Text style={styles.sectionDescription}>
        {t('payments_desc') || 'Connect your Stripe account to receive payments directly'}
      </Text>

      {/* Stripe Connect Status */}
      <View style={styles.stripeStatusCard}>
        <View style={styles.stripeHeader}>
          <Text style={styles.stripeTitle}>Stripe Connect</Text>
          {data.stripeConnectAccountId ? (
            <View style={[styles.statusBadge, data.stripeChargesEnabled ? styles.statusActive : styles.statusPending]}>
              <Text style={styles.statusBadgeText}>
                {data.stripeChargesEnabled ? (t('active') || 'Active') : (t('pending') || 'Pending')}
              </Text>
            </View>
          ) : (
            <View style={[styles.statusBadge, styles.statusInactive]}>
              <Text style={styles.statusBadgeText}>{t('not_connected') || 'Not Connected'}</Text>
            </View>
          )}
        </View>

        {data.stripeConnectAccountId ? (
          <>
            <View style={styles.stripeStatusGrid}>
              <View style={styles.stripeStatusItem}>
                {data.stripeChargesEnabled ? (
                  <Check size={16} color={theme.colors.merchant.primary} style={styles.stripeStatusIcon} />
                ) : (
                  <Circle size={16} color={theme.colors.text.secondary} style={styles.stripeStatusIcon} />
                )}
                <Text style={styles.stripeStatusLabel}>{t('charges_enabled') || 'Can accept payments'}</Text>
              </View>
              <View style={styles.stripeStatusItem}>
                {data.stripePayoutsEnabled ? (
                  <Check size={16} color={theme.colors.merchant.primary} style={styles.stripeStatusIcon} />
                ) : (
                  <Circle size={16} color={theme.colors.text.secondary} style={styles.stripeStatusIcon} />
                )}
                <Text style={styles.stripeStatusLabel}>{t('payouts_enabled') || 'Payouts enabled'}</Text>
              </View>
              <View style={styles.stripeStatusItem}>
                {data.stripeDetailsSubmitted ? (
                  <Check size={16} color={theme.colors.merchant.primary} style={styles.stripeStatusIcon} />
                ) : (
                  <Circle size={16} color={theme.colors.text.secondary} style={styles.stripeStatusIcon} />
                )}
                <Text style={styles.stripeStatusLabel}>{t('details_submitted') || 'Details submitted'}</Text>
              </View>
            </View>

            {!data.stripeChargesEnabled && (
              <TouchableOpacity
                style={[styles.stripeButton, styles.stripeButtonSecondary]}
                onPress={handleStripeConnect}
                disabled={connectingStripe}
              >
                {connectingStripe ? (
                  <Spinner size="small" />
                ) : (
                  <Text style={styles.stripeButtonSecondaryText}>
                    {t('complete_setup') || 'Complete Setup'}
                  </Text>
                )}
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.stripeButton}
              onPress={handleStripeDashboard}
              disabled={loadingDashboard}
            >
              {loadingDashboard ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.stripeButtonText}>
                  {t('open_stripe_dashboard') || 'Open Stripe Dashboard'}
                </Text>
              )}
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.stripeExplanation}>
              {t('stripe_connect_explanation') || 'Connect your Stripe account to receive reservation payments and in-store card payments directly to your bank account.'}
            </Text>
            <View style={styles.stripeFeatures}>
              <Text style={styles.stripeFeature}>• {t('receive_payments') || 'Receive payments directly'}</Text>
              <Text style={styles.stripeFeature}>• {t('automatic_transfers') || 'Automatic bank transfers'}</Text>
              <Text style={styles.stripeFeature}>• {t('terminal_support') || 'Use Stripe Terminal for card payments'}</Text>
            </View>
            <TouchableOpacity
              style={styles.stripeButton}
              onPress={handleStripeConnect}
              disabled={connectingStripe}
            >
              {connectingStripe ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.stripeButtonText}>
                  {t('connect_stripe') || 'Connect Stripe Account'}
                </Text>
              )}
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('settings') || 'Settings'}</Text>
      </View>

      {/* Section Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsContainer} contentContainerStyle={styles.tabsContent}>
        {renderSectionTab('basic', t('basic_info') || 'Basic')}
        {renderSectionTab('services', t('services') || 'Services')}
        {renderSectionTab('payments', t('payments') || 'Payments')}
        {renderSectionTab('images', t('images') || 'Images')}
        {renderSectionTab('hours', t('opening_hours') || 'Hours')}
        {renderSectionTab('contact', t('contact') || 'Contact')}
        {renderSectionTab('amenities', t('amenities') || 'Amenities')}
      </ScrollView>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {activeSection === 'basic' && renderBasicSection()}
        {activeSection === 'services' && renderServicesSection()}
        {activeSection === 'payments' && renderPaymentsSection()}
        {activeSection === 'images' && renderImagesSection()}
        {activeSection === 'hours' && renderHoursSection()}
        {activeSection === 'contact' && renderContactSection()}
        {activeSection === 'amenities' && renderAmenitiesSection()}

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.buttonDisabled]}
          onPress={saveMarket}
          disabled={saving}
        >
          {saving ? (
            <Spinner color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>{t('save_changes') || 'Save Changes'}</Text>
          )}
        </TouchableOpacity>

        {/* Delete Link - only in basic section */}
        {activeSection === 'basic' && (
          <TouchableOpacity
            style={styles.deleteLink}
            onPress={deleteMarket}
            disabled={deleting}
          >
            {deleting ? (
              <Spinner size="small" />
            ) : (
              <Text style={styles.deleteLinkText}>{t('delete_market') || 'Delete Market'}</Text>
            )}
          </TouchableOpacity>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  tabsContainer: {
    backgroundColor: theme.colors.background.card,
    maxHeight: 60,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  tabsContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8 },
  sectionTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.background.secondary,
    marginRight: 8 },
  sectionTabActive: {
    backgroundColor: theme.colors.merchant.primary },
  sectionTabText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  sectionTabTextActive: {
    color: '#fff' },
  content: {
    flex: 1,
    padding: 16 },
  section: {
    marginBottom: 16 },
  sectionDescription: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 16 },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.background.card,
    padding: 16,
    borderRadius: theme.borderRadius.md,
    marginBottom: 16 },
  switchInfo: {
    flex: 1,
    marginRight: 16 },
  switchLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  switchHint: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  inputGroup: {
    marginBottom: 20 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  textArea: {
    height: 100,
    textAlignVertical: 'top' },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6 },
  rowInputs: {
    flexDirection: 'row' },
  imageUploadBox: {
    width: 120,
    height: 120,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.card,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden' },
  coverUploadBox: {
    width: '100%',
    height: 160,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.card,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden' },
  logoPreview: {
    width: '100%',
    height: '100%' },
  coverPreview: {
    width: '100%',
    height: '100%' },
  imagePlaceholder: {
    alignItems: 'center' },
  imagePlaceholderIconContainer: {
    marginBottom: 8,
    opacity: 0.5 },
  imagePlaceholderText: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  hoursRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md },
  dayLabel: {
    width: 80 },
  dayText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary,
    textTransform: 'capitalize' },
  hoursInputs: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center' },
  timeInput: {
    width: 70,
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.sm,
    padding: 8,
    fontSize: 14,
    textAlign: 'center',
    color: theme.colors.text.primary },
  timeSeparator: {
    marginHorizontal: 8,
    fontSize: 16,
    color: theme.colors.text.secondary },
  closedText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    fontStyle: 'italic' },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10 },
  amenityItem: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.borderLight },
  amenityItemSelected: {
    borderColor: theme.colors.merchant.primary,
    backgroundColor: theme.colors.merchant.background },
  amenityIconContainer: {
    marginRight: 10 },
  amenityLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    flex: 1 },
  amenityLabelSelected: {
    color: theme.colors.merchant.primary,
    fontWeight: '600' },
  saveButton: {
    backgroundColor: theme.colors.merchant.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center',
    marginTop: 24,
    ...theme.shadows.medium },
  buttonDisabled: {
    opacity: 0.6 },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600' },
  deleteLink: {
    alignItems: 'center',
    marginTop: 32,
    padding: 12 },
  deleteLinkText: {
    color: theme.colors.text.secondary,
    fontSize: 14,
    textDecorationLine: 'underline' },
  bottomSpacer: {
    height: 100 },
  stripeStatusCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 20,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight },
  stripeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16 },
  stripeTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full },
  statusActive: {
    backgroundColor: '#dcfce7' },
  statusPending: {
    backgroundColor: '#fef3c7' },
  statusInactive: {
    backgroundColor: theme.colors.background.secondary },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.text.primary },
  stripeStatusGrid: {
    marginBottom: 16 },
  stripeStatusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8 },
  stripeStatusIcon: {
    marginRight: 10 },
  stripeStatusLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  stripeExplanation: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    lineHeight: 20,
    marginBottom: 16 },
  stripeFeatures: {
    marginBottom: 20 },
  stripeFeature: {
    fontSize: 14,
    color: theme.colors.text.primary,
    marginBottom: 6 },
  stripeButton: {
    backgroundColor: '#635bff',
    borderRadius: theme.borderRadius.md,
    padding: 14,
    alignItems: 'center',
    marginTop: 8 },
  stripeButtonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: '#635bff' },
  stripeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  stripeButtonSecondaryText: {
    color: '#635bff',
    fontSize: 16,
    fontWeight: '600' },
  // Service mode card styles
  serviceModeCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.borderLight },
  serviceModeCardActive: {
    borderColor: theme.colors.merchant.primary,
    backgroundColor: theme.colors.merchant.background },
  serviceModeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between' },
  serviceModeIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: theme.colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  serviceModeIconContainerActive: {
    backgroundColor: theme.colors.merchant.primary },
  serviceModeInfo: {
    flex: 1 },
  serviceModeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  serviceModeDesc: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    lineHeight: 18 },
  serviceModeExpanded: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight } })
